import Highcharts from 'highcharts';
import { useMemo } from 'react';
import { CalculateSeasonalMatrixResponse } from '../../../../Generated/Mark-It-View-Api';
import { StoneXRow } from '../../../StoneX';
import { StoneXToggleButton } from '../../../StoneXMui';
import { MatrixTermResult } from '../Matrix/MatrixTypes';
import { buildSeasonalMatrixBands, getMatrixMedian } from './SeasonalMatrixBands';
import SeasonalMatrixChart from './SeasonalMatrixChart';
import SeasonalMatrixPriceTable from './SeasonalMatrixPriceTable';
import { narrowPoints, narrowVolatility } from './SeasonalMatrixTypes';
import SeasonalMatrixVolatilityGauge from './SeasonalMatrixVolatilityGauge';
import { useSeasonalMatrixBandSelection } from './useSeasonalMatrixBandSelection';

interface SeasonalMatrixResultsProps {
  response: CalculateSeasonalMatrixResponse;
  matrix: MatrixTermResult | null;
  title: string;
  targetLabel: string;
  currencySymbol: string;
  averagesOnSecondAxis: boolean;
  onAveragesOnSecondAxisChange: (value: boolean) => void;
  onChartReady: (chart: Highcharts.Chart) => void;
}

// The volatility gauge on the left, the chart in the middle and the Price Matrix on the right, as on the Forecast and Hedge Plan screens.
export default function SeasonalMatrixResults(props: SeasonalMatrixResultsProps) {
  const { response, matrix, title, targetLabel, currencySymbol, averagesOnSecondAxis, onAveragesOnSecondAxisChange, onChartReady } = props;

  const currentYear = useMemo(() => narrowPoints(response.currentYear), [response]);
  const matrixBands = useMemo(() => (matrix ? buildSeasonalMatrixBands(matrix) : []), [matrix]);
  const lastClosePrice = currentYear[currentYear.length - 1]?.price;
  const bandSelection = useSeasonalMatrixBandSelection(matrixBands, lastClosePrice);

  return (
    <div style={{ display: 'flex', gap: '16px', alignItems: 'stretch' }}>
      <SeasonalMatrixVolatilityGauge volatility={narrowVolatility(response.volatility)} />
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div style={{ height: '500px' }}>
          <SeasonalMatrixChart
            title={title}
            currentYear={currentYear}
            threeYearAverage={narrowPoints(response.threeYearAverage)}
            fiveYearAverage={narrowPoints(response.fiveYearAverage)}
            forecastAverage={response.forecastAverage ?? null}
            currencySymbol={currencySymbol}
            averagesOnSecondAxis={averagesOnSecondAxis}
            bands={bandSelection.chartedBands}
            onHoveredPriceChange={bandSelection.hoverPrice}
            onChartReady={onChartReady}
          />
        </div>
        <StoneXRow>
          <StoneXToggleButton label="3 & 5 Year on Second Axis" value={averagesOnSecondAxis} onChange={onAveragesOnSecondAxisChange} />
        </StoneXRow>
      </div>
      {matrix && (
        <SeasonalMatrixPriceTable
          columnLabel={targetLabel}
          mean={matrix.weightedMean}
          median={getMatrixMedian(matrix)}
          bands={matrixBands}
          highlightedPercentile={bandSelection.highlightedPercentile}
          pinnedPercentiles={bandSelection.pinnedPercentiles}
          onBandClick={bandSelection.toggleBand}
          onBandHover={bandSelection.hoverBand}
          onBandHoverEnd={() => bandSelection.hoverBand(undefined)}
        />
      )}
    </div>
  );
}
